import * as React from 'react';
import { ClearButton } from '../../../../components/button/ClearButton';
import { SinglewideLine } from '../../../../modules/ordering/models/singlewide-production';
import { OrderingSvc } from '../../../../modules/ordering/service';
import { ProductionLine, ProductionLineMetadata } from '../../../../production/models/enums/production-line';
import { useTileData } from './use-tile-data';

const Months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Production is a week behind the cutoff
const DaysBehindCutoff = 7;
const DayInMilliseconds = 24 * 60 * 60 * 1000;

const day = (date: Date) => `${ Months[date.getMonth()] } ${ date.getDate() }`;

/**
 * A stretch of days, written the short way: "Oct 7 to 13", or "Oct 28 to Nov 3" when it crosses a month
 */
export const dayRange = (start: Date, end: Date) => {
    return start.getMonth() === end.getMonth()
        ? `${ day(start) } to ${ end.getDate() }`
        : `${ day(start) } to ${ day(end) }`;
};

const daysFrom = (date: Date, days: number) => new Date(date.getTime() + days * DayInMilliseconds);

/**
 * How dark a number is drawn, from 1 for the least up to 4 for the most there is, and 0 for nothing
 */
export const heat = (value: number, most: number) => {
    return value <= 0 || most <= 0 ? 0 : Math.min(4, Math.ceil(value / most * 4));
};

const lineName = (line: ProductionLine) => (ProductionLineMetadata as any)[line]?.display ?? ProductionLine[line];
const total = (line: SinglewideLine) => line.pastDue + line.singles.reduce((sum, x) => sum + x, 0);

/**
 * The singlewides still to be produced on each line that assembles sections, by the week they are produced in, which is
 * the production week of the cutoff date plus a week.  What was not produced in its week is past due.
 */
export const SinglewideProductionTile = () => {

    const [service] = React.useState(() => new OrderingSvc());
    const { data: production, failed, reload } = useTileData('singlewide-production', () => service.getSinglewideProduction());

    const weeks = production?.weeks ?? [];
    const lines = production?.lines ?? [];
    const weekTotals = weeks.map((_, i) => lines.reduce((sum, x) => sum + (x.singles[i] ?? 0), 0));
    const pastDueTotal = lines.reduce((sum, x) => sum + x.pastDue, 0);
    const grandTotal = lines.reduce((sum, x) => sum + total(x), 0);

    // each of the two is drawn against the most of its own kind
    const mostPastDue = Math.max(0, ...lines.map(x => x.pastDue));
    const mostInAWeek = Math.max(0, ...lines.flatMap(x => x.singles));

    const body = () => {
        if (failed) {
            return <div className="dashboard-tile-message">
                <p>Couldn't load the singlewides to produce.</p>
                <ClearButton id="singlewides-retry" text="Try again" icon="" classes="action" onBtnClick={ reload } />
            </div>;
        }

        if (!production) {
            return <p className="dashboard-tile-message">Loading…</p>;
        }

        if (grandTotal === 0) {
            return <p className="dashboard-tile-message">Nothing is left to produce in these weeks.</p>;
        }

        return <React.Fragment>
            <div className="singlewide-scroll">
                <table className="singlewide-table">
                    <thead>
                        <tr>
                            <th scope="col">Assembly line</th>
                            <th scope="col" className="singlewide-past-due"
                                title={ weeks.length > 0 ? `Cutoffs before ${ day(daysFrom(weeks[0].start, -DaysBehindCutoff)) }` : undefined }>
                                Past due
                            </th>
                            {
                                weeks.map((week, i) => (
                                    <th scope="col" key={ week.start.getTime() } className="singlewide-week"
                                        title={ `Cutoffs ${ dayRange(daysFrom(week.start, -DaysBehindCutoff), daysFrom(week.end, -DaysBehindCutoff)) }` }>
                                        { dayRange(week.start, week.end) }
                                        { i === 0 ? <span className="singlewide-week-note">this week</span> : null }
                                    </th>
                                ))
                            }
                            <th scope="col">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            lines.map(line => (
                                <tr key={ line.productionLine }>
                                    <th scope="row">{ lineName(line.productionLine) }</th>
                                    <td className={ `singlewide-cell singlewide-late-${ heat(line.pastDue, mostPastDue) }` }>
                                        { line.pastDue.toLocaleString() }
                                    </td>
                                    {
                                        weeks.map((week, i) => (
                                            <td key={ week.start.getTime() }
                                                className={ `singlewide-cell singlewide-heat-${ heat(line.singles[i] ?? 0, mostInAWeek) }` }>
                                                { (line.singles[i] ?? 0).toLocaleString() }
                                            </td>
                                        ))
                                    }
                                    <td className="singlewide-total">{ total(line).toLocaleString() }</td>
                                </tr>
                            ))
                        }
                    </tbody>
                    <tfoot>
                        <tr>
                            <th scope="row">All lines</th>
                            <td className="singlewide-total">{ pastDueTotal.toLocaleString() }</td>
                            { weekTotals.map((x, i) => <td key={ i } className="singlewide-total">{ x.toLocaleString() }</td>) }
                            <td className="singlewide-total">{ grandTotal.toLocaleString() }</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <p className="dashboard-tile-note">
                Production weeks run Wednesday to Tuesday and come a week after the cutoff date. Past due is what was not
                produced in its week.
            </p>
        </React.Fragment>;
    };

    return <section className="dashboard-tile dashboard-tile-wide singlewide-production" aria-labelledby="singlewide-production-heading">
        <div className="dashboard-tile-heading">
            <h2 id="singlewide-production-heading">Singlewide production</h2>
            {
                production && grandTotal > 0
                    ? <span className="dashboard-tile-total">{ grandTotal.toLocaleString() } { grandTotal === 1 ? 'singlewide' : 'singlewides' } to produce</span>
                    : null
            }
        </div>
        <p className="dashboard-tile-scope">Singlewides in orders that are not produced yet, by the week they are produced in</p>
        { body() }
    </section>;
};
